import {
  Checkbox,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
} from "@mui/material";
import { FormikProps, getIn } from "formik";
import CustomTextArea from "../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../components/Flex";
import Text from "../../../../../../../../../components/Text";
import { Form2Payload } from "../../../../../../scheduling/appointment/encounter/encounter-constant";

// All DBT fields live nested under `psychotherapyDbtVisitNote`.
// Option flags are named `${prefix}${suffix}` (e.g. `mindfulnessObserve`);
// each section's Client Response is stored at `responseKey`.
const DBT_NOTE_PATH = "psychotherapyDbtVisitNote";

// Compact spacing for the many checkbox / radio controls.
const COMPACT_CONTROL_SX = { my: -0.25, mr: 1 } as const;
const COMPACT_INPUT_SX = { py: 0.25 } as const;

export const DBT_SECTIONS = [
  {
    label: "Mindfulness",
    prefix: "mindfulness",
    responseKey: "mindfulnessComponent",
    items: [
      { suffix: "Observe", label: "Observe" },
      { suffix: "Describe", label: "Describe" },
      { suffix: "Participate", label: "Participate" },
      { suffix: "NonJudgmentally", label: "Non-judgmentally" },
      { suffix: "OneMindfully", label: "One-Mindfully" },
      { suffix: "Effectiveness", label: "Effectiveness" },
    ],
  },
  {
    label: "Emotion Regulation",
    prefix: "emotionRegulation",
    responseKey: "emotionRegulation",
    items: [
      { suffix: "EmotionIdentification", label: "Emotion identification" },
      {
        suffix: "IdentifyingVulnerabilityFactors",
        label: "Identifying vulnerability factors",
      },
      { suffix: "CheckTheFacts", label: "Check the Facts" },
      { suffix: "OppositeAction", label: "Opposite Action" },
      { suffix: "PleaseSkills", label: "PLEASE Skills" },
      {
        suffix: "BuildingPositiveExperiences",
        label: "Building Positive Experiences",
      },
      {
        suffix: "ReducingEmotionalVulnerability",
        label: "Reducing Emotional Vulnerability",
      },
      {
        suffix: "ManagingEmotionalIntensity",
        label: "Managing Emotional Intensity",
      },
    ],
  },
  {
    label: "Distress Tolerance",
    prefix: "distressTolerance",
    responseKey: "distressTolerance",
    items: [
      { suffix: "StopSkill", label: "STOP Skill" },
      { suffix: "TippSkills", label: "TIPP Skills" },
      { suffix: "AcceptsSkills", label: "ACCEPTS Skills" },
      { suffix: "SelfSoothing", label: "Self-Soothing" },
      { suffix: "ImproveTheMoment", label: "IMPROVE the Moment" },
      { suffix: "ProsAndCons", label: "Pros and Cons" },
      { suffix: "RadicalAcceptance", label: "Radical Acceptance" },
      {
        suffix: "WillingnessVsWillfulness",
        label: "Willingness vs. Willfulness",
      },
    ],
  },
  {
    label: "Interpersonal Effectiveness",
    prefix: "interpersonalEffectiveness",
    responseKey: "interpersonalEffectiveness",
    items: [
      { suffix: "DearMan", label: "DEAR MAN" },
      { suffix: "Give", label: "GIVE" },
      { suffix: "Fast", label: "FAST" },
      { suffix: "BoundarySetting", label: "Boundary Setting" },
      { suffix: "AssertivenessTraining", label: "Assertiveness Training" },
      { suffix: "ValidationStrategies", label: "Validation Strategies" },
      { suffix: "ConflictResolution", label: "Conflict Resolution" },
      {
        suffix: "RelationshipEffectiveness",
        label: "Relationship Effectiveness",
      },
    ],
  },
  {
    label: "Behavioral Analysis",
    prefix: "behavioralAnalysis",
    responseKey: "behavioralAnalysis",
    items: [
      { suffix: "ChainAnalysis", label: "Chain Analysis" },
      { suffix: "SolutionAnalysis", label: "Solution Analysis" },
      { suffix: "DiaryCardReview", label: "Diary Card Review" },
      { suffix: "TargetBehaviorReview", label: "Target Behavior Review" },
      { suffix: "BehavioralRehearsal", label: "Behavioral Rehearsal" },
      { suffix: "SkillsCoaching", label: "Skills Coaching" },
    ],
  },
] as const;

// Target Behaviors single-select (stored on psychotherapyDbtVisitNote.targetBehaviors)
export const DBT_TARGET_BEHAVIOR_OPTIONS = [
  {
    value: "LIFE_THREATENING_BEHAVIORS",
    label: "Life-Threatening Behaviors",
  },
  {
    value: "THERAPY_INTERFERING_BEHAVIORS",
    label: "Therapy-Interfering Behaviors",
  },
  {
    value: "QUALITY_OF_LIFE_INTERFERING_BEHAVIORS",
    label: "Quality-of-Life Interfering Behaviors",
  },
  { value: "SKILLS_ACQUISITION_GOALS", label: "Skills Acquisition Goals" },
] as const;

// Progress single-select (stored on psychotherapyDbtVisitNote.initialProgress)
export const DBT_PROGRESS_OPTIONS = [
  // "Initial Session" removed per BA. This array drives both the form radios
  // and the summary/PDF label lookup, so removing it here covers both.
  // { value: "INITIAL_SESSION", label: "Initial Session" },
  { value: "MINIMAL_PROGRESS", label: "Minimal Progress" },
  { value: "MODERATE_PROGRESS", label: "Moderate Progress" },
  { value: "SIGNIFICANT_PROGRESS", label: "Significant Progress" },
] as const;

interface DbtComponentsProps {
  formik: FormikProps<Form2Payload>;
}

const DbtComponents = ({ formik }: DbtComponentsProps) => (
  <Grid size={12}>
    <Text variant="subtitle1" fontWeight={600} mb={1}>
      DBT-Specific Components
    </Text>
    <Text variant="body1" fontWeight={600} mb={0.5}>
      DBT Interventions Provided
    </Text>

    <Flex flexDirection="column" gap={1} width="100%">
      {DBT_SECTIONS.map((section) => {
        const responseKey = `${DBT_NOTE_PATH}.${section.responseKey}`;
        return (
          <Flex
            key={section.prefix}
            flexDirection="column"
            gap={0.5}
            width="100%"
          >
            <Text variant="body2" fontWeight={600}>
              {section.label}
            </Text>

            <Grid container spacing={0}>
              {section.items.map((item) => {
                const fieldKey = `${DBT_NOTE_PATH}.${section.prefix}${item.suffix}`;
                const checked = !!getIn(formik.values, fieldKey);
                return (
                  <Grid
                    key={item.suffix}
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 4,
                    }}
                  >
                    <FormControlLabel
                      sx={COMPACT_CONTROL_SX}
                      control={
                        <Checkbox
                          size="small"
                          sx={COMPACT_INPUT_SX}
                          name={fieldKey}
                          checked={checked}
                          onChange={(e) =>
                            formik.setFieldValue(fieldKey, e.target.checked)
                          }
                        />
                      }
                      label={<Text variant="body2">{item.label}</Text>}
                    />
                  </Grid>
                );
              })}
            </Grid>

            <CustomTextArea
              label="Client Response"
              rows={2}
              placeholder="Client Response"
              value={getIn(formik.values, responseKey) ?? ""}
              onChange={(e) =>
                formik.setFieldValue(responseKey, e.target.value)
              }
            />
          </Flex>
        );
      })}
    </Flex>

    {/* Target Behaviors */}
    <Text variant="body1" fontWeight={600} mt={1.5} mb={0.5}>
      Target Behaviors
    </Text>
    <RadioGroup
      row
      name={`${DBT_NOTE_PATH}.targetBehaviors`}
      value={getIn(formik.values, `${DBT_NOTE_PATH}.targetBehaviors`) ?? ""}
      onChange={(e) =>
        formik.setFieldValue(`${DBT_NOTE_PATH}.targetBehaviors`, e.target.value)
      }
    >
      {DBT_TARGET_BEHAVIOR_OPTIONS.map((option) => (
        <FormControlLabel
          key={option.value}
          sx={COMPACT_CONTROL_SX}
          value={option.value}
          control={<Radio size="small" sx={COMPACT_INPUT_SX} />}
          label={<Text variant="body2">{option.label}</Text>}
        />
      ))}
    </RadioGroup>

    {/* Progress */}
    <Text variant="body1" fontWeight={600} mt={1.5} mb={0.5}>
      Progress
    </Text>
    <RadioGroup
      row
      name={`${DBT_NOTE_PATH}.initialProgress`}
      value={getIn(formik.values, `${DBT_NOTE_PATH}.initialProgress`) ?? ""}
      onChange={(e) =>
        formik.setFieldValue(`${DBT_NOTE_PATH}.initialProgress`, e.target.value)
      }
    >
      {DBT_PROGRESS_OPTIONS.map((option) => (
        <FormControlLabel
          key={option.value}
          sx={COMPACT_CONTROL_SX}
          value={option.value}
          control={<Radio size="small" sx={COMPACT_INPUT_SX} />}
          label={<Text variant="body2">{option.label}</Text>}
        />
      ))}
    </RadioGroup>
  </Grid>
);

export default DbtComponents;
